<template>
  <div
    :class="[
      `ritm-tree simple ${this.module}`,
      { dark: theme === 'dark-theme' },
      { cicled },
      { bordered }
    ]"
    v-loading="isLoading"
  >
    <div class="ritm-tree__title" v-if="title">
      <r-text type="caption">{{ title }}</r-text>
    </div>
    <div class="tree-controls">
      <r-search-input
        :filter-text="filterText"
        :is-loading="filterLoading"
        @change="e => filterChange(e)"
      />
    </div>
    <div :id="module" class="tree-wrapper"></div>
  </div>
</template>

<script>
import InfiniteTree from "infinite-tree";
import renderer from "./renderer.js";
import debounce from "lodash.debounce";

export default {
  props: {
    title: String,
    module: String,
    data: Array,
    isLoading: Boolean,
    bordered: Boolean,
    cicled: Boolean,
    "edit-mode": Boolean,
    "show-tools": Boolean,
    "node-click-handler": Function,
    tools: { type: Array, default: () => [] }
  },
  data() {
    return {
      mouseTarget: {},
      tooltipElem: null,
      preventTooltip: false,

      token: this.$store.state.auth.token,
      currentProfileId: this.$store.state.profiles.currentProfile.id,
      tree: {},
      droppable: false,

      filterText: "",
      filterLoading: false,

      renameModeNodeId: "",
      newNodeName: ""
    };
  },
  watch: {
    filterText(val) {
      this.filterLoading = true;
      this.filterTree(val);
    },
    data(val) {
      this.tree.loadData(val);
      setTimeout(() => this.filterTree(this.filterText), 100);
    },
    renameModeNodeId(val) {
      if (val) {
        const currentNode = this.tree.getNodeById(val);

        const input = document
          .querySelector(`#${this.module}`)
          .querySelector(".ritm-input");
        input.addEventListener("click", e => this.inputStopEvent(e));
        input.addEventListener("keydown", e => this.inputOnKeyDown(e));
        input.addEventListener("blur", e => this.inputOnBlur(e));
        input.focus();
        input.value = currentNode.name;
      }
    }
  },
  computed: {
    theme() {
      return this.$store.state.theme;
    }
  },
  methods: {
    filterChange(e) {
      this.filterText = e;
    },
    refreshData() {
      this.filterTree(this.filterText);
    },
    filterTree: debounce(function(value) {
      this.tree.filter(value, { includeDescendants: false });

      this.filterLoading = false;
    }, 200),
    enterRenameMode(nodeId) {
      const currentNode = this.tree.getNodeById(nodeId);

      this.renameModeNodeId = nodeId;
      this.tree.updateNode(currentNode);
    },
    exitRenameMode(e, nodeId) {
      e.stopPropagation();
      const currentNode = this.tree.getNodeById(nodeId);

      this.renameModeNodeId = "";
      this.tree.updateNode(currentNode);
    },
    saveDsName(e, node) {
      e.stopPropagation();
      if (node.name !== this.newNodeName) {
        const changedLayer = [{ id: node.id, name: this.newNodeName }];
        this.updateProfileTreeItem(changedLayer);
        node.name = this.newNodeName;
      }
      this.renameModeNodeId = "";
      this.newNodeName = "";
      this.exitRenameMode(e, node.id);
    },
    inputOnKeyDown(e) {
      const target = e.target || e.srcElement;
      this.newNodeName = target.value;
      const currentNode = this.tree.getNodeById(target.getAttribute("data-id"));

      if (e.key === "Enter") this.saveDsName(e, currentNode);
      if (e.key === "Escape") this.exitRenameMode(e, this.renameModeNodeId);
    },
    inputOnBlur(e) {
      if (this.renameModeNodeId) {
        const target = e.target || e.srcElement;
        this.newNodeName = target.value;
        const currentNode = this.tree.getNodeById(
          target.getAttribute("data-id")
        );
        this.saveDsName(e, currentNode);
      }
    },
    inputStopEvent(e) {
      e.stopPropagation();
    },
    clickHandler(e) {
      const target = e.target || e.srcElement;
      const nodeId = target.getAttribute("data-id");
      const nodeName = target.getAttribute("data-name");
      const nodeType = target.getAttribute("node-type");
      const nodeAction = target.getAttribute("node-action");

      this.removeTooltip();

      switch (nodeType) {
        case "tools":
          this.nodeClickHandler(nodeAction, nodeId);
          break;
        case "title":
          this.nodeClickHandler("settings", nodeId, nodeName);
          break;
      }
    },
    showTooltip: debounce(function() {
      if (!this.mouseTarget || this.preventTooltip) return;
      const nodeId = this.mouseTarget.getAttribute("data-id");
      const node = this.tree.getNodeById(nodeId);

      if (!node) return;
      const nodeName = node.name;

      let tooltipHtml = nodeName;

      if (!tooltipHtml) return;
      this.tooltipElem = document.createElement("div");
      this.tooltipElem.className = "ritm-tree__tooltip";
      this.tooltipElem.innerHTML = tooltipHtml;
      document.body.append(this.tooltipElem);

      let coords = this.mouseTarget.getBoundingClientRect();
      let left = coords.left + this.mouseTarget.offsetWidth + 8;
      if (left < 0) left = 0;
      let top = coords.top - this.tooltipElem.offsetHeight + 28;
      if (top < 0) {
        top = coords.top + this.mouseTarget.offsetHeight + 28;
      }

      this.tooltipElem.style.left = left + "px";
      this.tooltipElem.style.top = top + "px";
    }, 1000),
    removeTooltip() {
      this.preventTooltip = true;
      if (this.tooltipElem) {
        this.tooltipElem.remove();
        this.tooltipElem = null;
      }
      let treeTooltips = document.querySelectorAll(".ritm-tree__tooltip");
      if (treeTooltips.length) treeTooltips.forEach(e => e.remove());
    },
    handleMouseOver(e) {
      const target = e.target || e.srcElement;

      this.mouseTarget = target.closest(".infinite-tree-item");
      if (!this.mouseTarget) {
        this.removeTooltip();
        return;
      }
      this.preventTooltip = false;
      this.showTooltip();
    }
  },
  mounted() {
    document
      .querySelector(`#${this.module}`)
      .addEventListener("dragstart", e => e.preventDefault());
    this.tree = new InfiniteTree({
      el: document.querySelector(`#${this.module}`),
      selectable: false,
      droppable: false,
      shouldLoadNodes: function(parentNode) {
        return !parentNode.hasChildren() && parentNode.loadNodes;
      },
      loadNodes: function(parentNode) {
        const nodes = [];
        nodes.length = 1000;
        for (let i = 0; i < nodes.length; ++i) {
          nodes[i] = {
            id: `${parentNode.id}.${i}`,
            name: `${parentNode.name}.${i}`,
            loadOnDemand: true
          };
        }
      },
      nodeIdAttr: "data-id",
      rowRenderer: (node, treeOptions) => renderer(node, treeOptions, this),
      noDataText: `${this.$t("no-data")}`
    });
    this.tree.on("click", e => this.clickHandler(e));
    this.tree.loadData(this.data);

    document
      .querySelector(`#${this.module}`)
      .addEventListener("mouseover", e => this.handleMouseOver(e));

    document
      .querySelector(`#${this.module}`)
      .addEventListener("mouseout", () => this.removeTooltip());
  }
};
</script>

<style lang="scss">
.ritm-tree {
  &.simple {
    border: 1px solid;
    box-sizing: border-box;
    border-radius: var(--border-radius);
    padding: 8px;
    background-color: var(--modal_overlay) !important;
    border-color: var(--dividers_high_contrast) !important;
    .tree-wrapper {
      margin-top: 4px;
      height: calc(100% - 64px);
      border: none !important;
      .infinite-tree {
        &-node {
          align-items: center;
        }
        &-content {
          padding: 4px;
        }
        &-toggler {
          padding: 0;
        }
      }
    }
  }
}
</style>

<i18n>
{
  "ru": {
    "no-data": "Данные отсутствуют"
  },
  "en": {
    "no-data": "No data"
  }
}
</i18n>
